import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import {
  Scale, LogOut, LayoutDashboard, MapPin, Briefcase, FileText, Search,
  Tag, UserRound, ShieldCheck, Menu, X, PanelLeftClose, PanelLeftOpen, Building2, Gavel, Sun, Moon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { getCountyPref } from "@/lib/countyPref";

import api from "@/lib/api";
import TopNav from "@/components/TopNav";
import { saveThemeToAccount, applyTheme } from "@/lib/theme";
import { useSosEnabled } from "@/lib/siteConfig";

function SideNav() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [county, setCounty] = useState(getCountyPref()?.county || null);
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("nc_sidebar_collapsed") === "1");
  const [dark, setDark] = useState(() => localStorage.getItem("nc_theme") !== "light");
  const [firmLogo, setFirmLogo] = useState(null);
  const [logoWhitewash, setLogoWhitewash] = useState(false);
  const [hideSos, setHideSos] = useState(false);
  const siteSosOn = useSosEnabled();

  // Firm logo + SOS visibility preference for signed-in members/owners.
  useEffect(() => {
    if (!user) { setFirmLogo(null); setHideSos(false); return; }
    api.get("/firm").then(({ data }) => setFirmLogo(data?.logo || null)).catch(() => {});
    api.get("/profile").then(({ data }) => setHideSos(!!data?.hide_sos)).catch(() => {});
  }, [user]);

  // Live-update SOS visibility when the user toggles it in settings.
  useEffect(() => {
    const sync = (e) => setHideSos(!!(e.detail ?? false));
    window.addEventListener("nc-sos-pref-changed", sync);
    return () => window.removeEventListener("nc-sos-pref-changed", sync);
  }, []);

  // On the navy sidebar, colored or dark firm logos are auto-rendered white for legibility.
  useEffect(() => {
    if (!firmLogo) { setLogoWhitewash(false); return; }
    const img = new Image();
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        const w = (c.width = 40), h = (c.height = 40);
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        const { data } = ctx.getImageData(0, 0, w, h);
        let opaque = 0, colorful = 0, nearWhite = 0;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] < 40) continue;
          opaque++;
          const r = data[i], g = data[i + 1], b = data[i + 2];
          const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
          const sat = mx === 0 ? 0 : (mx - mn) / mx;
          const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
          if (sat > 0.15) colorful++;
          if (lum > 0.7 && sat < 0.15) nearWhite++;
        }
        if (!opaque) { setLogoWhitewash(false); return; }
        // Whitewash colored logos, and dark logos that would be invisible on navy;
        // leave logos that are already predominantly white untouched.
        setLogoWhitewash(colorful / opaque > 0.08 || nearWhite / opaque < 0.6);
      } catch {
        setLogoWhitewash(true);
      }
    };
    img.onerror = () => setLogoWhitewash(false);
    img.src = firmLogo;
  }, [firmLogo]);


  useEffect(() => {
    const sync = () => setCounty(getCountyPref()?.county || null);
    window.addEventListener("nc-county-pref-changed", sync);
    return () => window.removeEventListener("nc-county-pref-changed", sync);
  }, []);

  // Shift page content to make room for the fixed sidebar (scoped, so auth pages are unaffected).
  useEffect(() => {
    const el = document.documentElement;
    el.classList.add("has-side-nav");
    return () => el.classList.remove("has-side-nav");
  }, []);

  // Reflect collapsed state so the content padding can match the icons-only width.
  useEffect(() => {
    const el = document.documentElement;
    el.classList.toggle("sidebar-collapsed", collapsed);
    localStorage.setItem("nc_sidebar_collapsed", collapsed ? "1" : "0");
    return () => el.classList.remove("sidebar-collapsed");
  }, [collapsed]);

  // Light/dark theme toggle.
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("nc_theme", dark ? "dark" : "light");
  }, [dark]);
  useEffect(() => {
    const sync = () => setDark(localStorage.getItem("nc_theme") !== "light");
    window.addEventListener("nc-theme-changed", sync);
    return () => window.removeEventListener("nc-theme-changed", sync);
  }, []);
  const toggleTheme = () => { const next = !dark; setDark(next); const p = next ? "dark" : "light"; applyTheme(p); if (user) saveThemeToAccount(p); };

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const publicLinks = [
    { to: "/forms", label: t("nav.courtForms"), icon: FileText, testid: "nav-forms" },
    ...(!hideSos && siteSosOn ? [{ to: "/sos-forms", label: t("nav.sosForms"), icon: Building2, testid: "nav-sos-forms" }] : []),
    { to: "/find", label: t("nav.finder"), icon: Search, testid: "nav-finder" },
    { to: "/find-attorney", label: t("nav.findAttorney"), icon: Gavel, testid: "nav-find-attorney" },
    { to: "/pricing", label: t("nav.pricing"), icon: Tag, testid: "nav-pricing" },
    { to: "/for-attorneys", label: t("nav.forAttorneys"), icon: Scale, testid: "nav-for-attorneys" },
  ];
  const workspaceLinks = user ? [
    { to: "/dashboard", label: t("nav.dashboard"), icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/matters", label: t("nav.matters"), icon: Briefcase, testid: "nav-matters" },
    { to: "/profile", label: t("nav.profile"), icon: UserRound, testid: "nav-profile" },
    ...(user.role === "admin" ? [{ to: "/admin", label: t("nav.admin"), icon: ShieldCheck, testid: "nav-admin" }] : []),
  ] : [];

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  const NavItem = ({ to, label, icon: Icon, testid }) => (
    <Link
      to={to}
      data-testid={testid}
      title={collapsed ? label : undefined}
      className={`flex items-center gap-3 rounded-sm transition-colors ${collapsed ? "md:justify-center md:px-0 px-3 py-2.5" : "px-3 py-2.5"} ${
        isActive(to) ? "bg-white/15 text-white font-semibold" : "text-white/70 hover:bg-white/10 hover:text-white"
      }`}
    >
      <Icon size={17} className="shrink-0" />
      <span className={collapsed ? "md:hidden" : ""}>{label}</span>
    </Link>
  );

  return (
    <>
      {/* Mobile top bar */}
      <div className="md:hidden no-print fixed top-0 inset-x-0 z-50 h-14 flex items-center justify-between px-4 border-b border-white/10 bg-primary text-primary-foreground">
        <Link to="/" data-testid="nav-logo-mobile" className="flex items-center gap-2">
          <span className="grid place-items-center w-8 h-8 bg-white text-primary"><Scale size={16} /></span>
          <span className="font-serif text-lg font-semibold tracking-tight text-white">NC Legal Forms</span>
        </Link>
        <button data-testid="nav-mobile-toggle" onClick={() => setOpen((o) => !o)}
          className="grid place-items-center w-9 h-9 border-2 border-white/40 text-white hover:bg-white/10 transition-colors">
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && <div data-testid="nav-mobile-overlay" className="md:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setOpen(false)} />}

      {/* Sidebar (mobile drawer is always full width; desktop can collapse to icons) */}
      <aside
        data-testid="side-nav"
        className={`no-print fixed z-50 top-0 left-0 h-screen w-60 ${collapsed ? "md:w-16" : "md:w-60"} flex flex-col border-r border-white/10 bg-primary text-primary-foreground transition-[transform,width] duration-300
          md:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        {/* Brand */}
        <Link to="/" data-testid="nav-logo" className={`flex items-center gap-2 h-16 shrink-0 border-b border-white/10 ${collapsed ? "md:justify-center md:px-0 px-5" : "px-5"}`}>
          <span className="grid place-items-center w-9 h-9 bg-white text-primary shrink-0"><Scale size={18} /></span>
          <span className={`font-serif text-lg font-semibold tracking-tight leading-none text-white ${collapsed ? "md:hidden" : ""}`}>
            NC Legal Forms
            <span className="block overline text-[0.5rem] text-accent tracking-[0.22em]">STATE OF NORTH CAROLINA</span>
          </span>
        </Link>

        {/* Firm logo for signed-in members */}
        {firmLogo && !collapsed && (
          <div className="hidden md:flex items-center justify-center px-4 py-3 border-b border-white/10" data-testid="nav-firm-logo">
            <img src={firmLogo} alt="Firm logo" className={`max-h-10 max-w-[150px] object-contain ${logoWhitewash ? "brightness-0 invert" : ""}`} data-testid="nav-firm-logo-img" />
          </div>
        )}

        {/* Links */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1 text-sm font-medium">
          {publicLinks.map((l) => <NavItem key={l.to} {...l} />)}

          {workspaceLinks.length > 0 && (
            <div className="pt-3 mt-2 border-t border-white/10">
              <p className={`px-3 pb-1 text-[0.6rem] font-semibold uppercase tracking-[0.15em] text-white/50 ${collapsed ? "md:hidden" : ""}`} data-testid="nav-workspace-heading">
                {t("nav.workspace")}
              </p>
              {workspaceLinks.map((l) => <NavItem key={l.to} {...l} />)}
            </div>
          )}
        </nav>

        {/* Footer controls */}
        <div className="shrink-0 border-t border-white/10 p-3 space-y-2">
          {/* Collapse toggle (desktop only) */}
          <button
            data-testid="nav-collapse-toggle"
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={`hidden md:flex items-center gap-2 w-full px-3 h-8 rounded-sm text-xs text-white/60 hover:bg-white/10 hover:text-white transition-colors ${collapsed ? "justify-center px-0" : ""}`}>
            {collapsed ? <PanelLeftOpen size={16} /> : <><PanelLeftClose size={16} /> {t("nav.collapse")}</>}
          </button>

          <button
            data-testid="nav-county-chip"
            onClick={() => window.dispatchEvent(new Event("open-county-gate"))}
            title={county ? t("nav.countyName", { name: county }) : t("nav.setCounty")}
            className={`w-full inline-flex items-center gap-2 h-9 rounded-sm border-2 border-white/30 text-white text-xs font-semibold hover:bg-accent hover:text-accent-foreground hover:border-accent transition-colors ${collapsed ? "md:justify-center md:px-0 px-3" : "px-3"}`}>
            <MapPin size={14} className="shrink-0" /> <span className={collapsed ? "md:hidden" : ""}>{county ? t("nav.countyName", { name: county }) : t("nav.setCounty")}</span>
          </button>

          <div className={`flex items-center justify-between gap-2 ${collapsed ? "md:justify-center" : ""}`}>
            <LanguageSwitcher />
            <button onClick={toggleTheme} data-testid="theme-toggle-btn" aria-label="Toggle theme"
              className="grid place-items-center w-8 h-8 rounded-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors shrink-0">
              {dark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            {user && <span className={`text-xs text-white/60 truncate ${collapsed ? "md:hidden" : ""}`} data-testid="nav-user-name">{user.name?.split(" ")[0]}</span>}
          </div>

          {user ? (
            <Button onClick={handleLogout} size="sm" variant="outline" data-testid="nav-logout-btn"
              title={collapsed ? t("nav.signout") : undefined}
              className={`w-full gap-2 rounded-sm bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white ${collapsed ? "md:px-0" : ""}`}>
              <LogOut size={16} className="shrink-0" /> <span className={collapsed ? "md:hidden" : ""}>{t("nav.signout")}</span>
            </Button>
          ) : (
            <div className="space-y-2">
              <Link to="/login" className="block"><Button variant="outline" size="sm" data-testid="nav-login-btn" title={collapsed ? t("nav.login") : undefined} className={`w-full rounded-sm bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white ${collapsed ? "md:px-0" : ""}`}><span className={collapsed ? "md:hidden" : ""}>{t("nav.login")}</span><UserRound size={15} className={`shrink-0 ${collapsed ? "hidden md:block" : "hidden"}`} /></Button></Link>
              <Link to="/pricing" className={`block ${collapsed ? "md:hidden" : ""}`}><Button size="sm" data-testid="nav-cta-btn" className="w-full rounded-sm bg-accent text-accent-foreground hover:bg-white hover:text-primary transition-colors">{t("nav.getAccess")}</Button></Link>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

// App-shell (signed-in workspace) routes use the left sidebar; every other
// (public / marketing) page uses the top nav bar.
const APP_PREFIXES = ["/dashboard", "/profile", "/saved", "/matters", "/admin", "/onboarding"];

export default function Navbar() {
  const location = useLocation();
  const isAppRoute = APP_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
  return isAppRoute ? <SideNav /> : <TopNav />;
}
